import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useD312Query } from './hooks/useD312Query';
import type { MovementRow } from '../../services/d312Api';
import './D312Form.css';

const categoryLabels: Record<number, string> = {
  1: '原物料',
  2: '托工',
  3: '全部',
  4: '代工入布',
};

const D312Form: React.FC = () => {
  const { loading, data, summary, queried, noData, runQuery, reset } = useD312Query();

  const [category, setCategory] = useState<number>(3);
  const [orderNoFrom, setOrderNoFrom] = useState('');
  const [orderNoTo, setOrderNoTo] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [productFrom, setProductFrom] = useState('');
  const [productTo, setProductTo] = useState('');

  const handleQuery = () => {
    runQuery({
      category,
      order_no_from: orderNoFrom.trim() || undefined,
      order_no_to: orderNoTo.trim() || undefined,
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
      product_from: productFrom.trim() || undefined,
      product_to: productTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setCategory(3);
    setOrderNoFrom('');
    setOrderNoTo('');
    setDateFrom('');
    setDateTo('');
    setProductFrom('');
    setProductTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const catLabel = categoryLabels[category] || '全部';
    const title = `繳庫進出明細表（${catLabel}）`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>${title}</h2>
<table><thead><tr><th>批號</th><th>顏色</th><th>入庫量</th><th>單據號碼</th><th>單據種類</th><th>庫位</th><th>單據日期</th><th>使用量</th><th>結存</th></tr></thead><tbody>
${data.map(r => `<tr><td>${formatBatchID(r.batch_id)}</td><td>${r.color_name}</td><td class="right">${r.inbound_qty}</td><td>${r.doc_no}</td><td>${r.movement_type}</td><td>${r.location_name}</td><td>${r.doc_date}</td><td class="right">${r.qty?.toFixed(2) || ''}</td><td class="right">${r.balance?.toFixed(2)}</td></tr>`).join('')}
</tbody></table>
${summary ? `<p style="margin-top:8px">入庫合計: ${summary.total_inbound?.toFixed(2)} | 出庫合計: ${summary.total_outbound?.toFixed(2)} | 最終結存: ${summary.total_balance?.toFixed(2)} | 批次: ${summary.batch_count} | 異動: ${summary.movement_count}</p>` : ''}
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<MovementRow> = [
    { title: '批號', dataIndex: 'batch_id', key: 'batch_id', width: 150,
      render: (v: string) => formatBatchID(v) },
    { title: '顏色', dataIndex: 'color_name', key: 'color_name', width: 120, ellipsis: true },
    { title: '入庫量', dataIndex: 'inbound_qty', key: 'inbound_qty', width: 90,
      align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '單據號碼', dataIndex: 'doc_no', key: 'doc_no', width: 110 },
    { title: '單據種類', dataIndex: 'movement_type', key: 'movement_type', width: 90 },
    { title: '庫位', dataIndex: 'location_name', key: 'location_name', width: 100, ellipsis: true },
    { title: '單據日期', dataIndex: 'doc_date', key: 'doc_date', width: 100 },
    { title: '使用量', dataIndex: 'qty', key: 'qty', width: 90,
      align: 'right' as const, render: (v: number) => v ? v.toFixed(2) : '' },
    { title: '結存', dataIndex: 'balance', key: 'balance', width: 90,
      align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.D312} action="query">
      <div className="d312-container">
        <div className="d312-header">
          <h2>繳庫進出明細表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('類別選擇')}
            <Radio.Group value={category} onChange={e => setCategory(e.target.value)}>
              <Radio.Button value={1}>原物料</Radio.Button>
              <Radio.Button value={2}>托工</Radio.Button>
              <Radio.Button value={3}>全部</Radio.Button>
              <Radio.Button value={4}>代工入布</Radio.Button>
            </Radio.Group>
          </div>
          <Space wrap size="middle">
            <div>
              {filterLabel('入庫單號起')}
              <Input value={orderNoFrom} onChange={e => setOrderNoFrom(e.target.value)}
                placeholder="起" style={{ width: 130 }} allowClear maxLength={8} />
            </div>
            <div>
              {filterLabel('入庫單號迄')}
              <Input value={orderNoTo} onChange={e => setOrderNoTo(e.target.value)}
                placeholder="迄" style={{ width: 130 }} allowClear maxLength={8} />
            </div>
            <div>
              {filterLabel('入庫日期起 (YYYY-MM-DD)')}
              <Input value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                placeholder="例 2026-01-01" style={{ width: 150 }} allowClear />
            </div>
            <div>
              {filterLabel('入庫日期迄 (YYYY-MM-DD)')}
              <Input value={dateTo} onChange={e => setDateTo(e.target.value)}
                placeholder="例 2026-12-31" style={{ width: 150 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號起')}
              <Input value={productFrom} onChange={e => setProductFrom(e.target.value)}
                placeholder="起" style={{ width: 130 }} allowClear maxLength={12} />
            </div>
            <div>
              {filterLabel('產品代號迄')}
              <Input value={productTo} onChange={e => setProductTo(e.target.value)}
                placeholder="迄" style={{ width: 130 }} allowClear maxLength={12} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery}
                  loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            {summary && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                入庫合計: {summary.total_inbound?.toFixed(2)} |
                出庫合計: {summary.total_outbound?.toFixed(2)} |
                最終結存: {summary.total_balance?.toFixed(2)} |
                批次: {summary.batch_count} |
                異動筆數: {summary.movement_count}
              </div>
            )}
            <Table<MovementRow>
              columns={columns}
              dataSource={data}
              rowKey={(r, i) => `${r.batch_id}-${r.doc_no}-${r.movement_type}-${i}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 1100 }}
              style={{ marginBottom: 16 }}
            />
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

/** BR-08: Format batch ID as XXXXXX-X-X-XX-XX */
function formatBatchID(id: string): string {
  if (!id || id.length < 12) return id || '';
  const trimmed = id.replace(/\s+$/, '');
  if (trimmed.length < 6) return trimmed;
  return `${trimmed.substring(0, 6)}-${trimmed.substring(6, 7)}-${trimmed.substring(7, 8)}-${trimmed.substring(8, 10)}-${trimmed.substring(10, 12)}`;
}

export default D312Form;
